/**
 * YetkiFirmaKullaniciSekmesi.jsx — Yetki Yönetimi › Firma–Kullanıcı (backlog #309, 2026-10-10)
 *
 * Bir firma seçilir; o firmaya bağlı kullanıcılar (user_to_firma) listelenir. Buradan bir kullanıcı
 * firmaya bağlanır ya da firmadan çıkarılır. Bir firma kullanıcısının hangi firmanın verisini gördüğü
 * bu bağlantıyla belirlenir (backend tenancy: PersonnelService.firmaScopeFor).
 * Backend: GET /rbac/users?firmaId=, PUT /rbac/users/{id}/firma, DELETE /rbac/users/{id}/firma/{firmaId}.
 * Bağlama, kullanıcının diğer firma bağlantılarını kaldırır: bir kullanıcının tek firması olur.
 */
import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Row, Col, Button, Alert, Spinner, Input, Label, Table, Badge } from "reactstrap";
import { Building2, Link2, X } from "lucide-react";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import { listManagedUsers, setUserFirma, removeUserFirma } from "../../../services/rbacService";
import { RolRozeti, FirmaHucresi } from "./YetkiKullanicilarSekmesi";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(currentLocaleTag());
};
const fullName = (u) => [u.userName, u.userSurname].filter(Boolean).join(" ") || u.userLoginName || `#${u.userId}`;

export default function YetkiFirmaKullaniciSekmesi({ me, onReload }) {
  const { t: tx } = useTranslation("ayarlar");
  const [firma, setFirma] = useState(null);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  // Kullanıcı bağlama araması
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [adaylar, setAdaylar] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!firma?.firmaId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const out = await listManagedUsers({ firmaId: firma.firmaId, size: 200 });
        if (!ignore) setUsers(out?.users || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [firma, reloadToken]);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (query.length < 2) return undefined;
    let ignore = false;
    async function run() {
      try {
        const out = await listManagedUsers({ q: query, size: 10 });
        if (!ignore) setAdaylar(out?.users || []);
      } catch (e) {
        if (!ignore) setError(e.message);
      }
    }
    run();
    return () => { ignore = true; };
  }, [query]);

  async function act(key, fn, okText) {
    setBusy(key);
    setError(null);
    setMsg(null);
    try {
      await fn();
      setMsg(okText);
      setReloadToken((n) => n + 1);
      setQ("");
      setAdaylar([]);
      await onReload?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  const isMe = (u) => !me?.root && me?.userId === u.userId;
  const visibleAdaylar = query.length >= 2 ? adaylar : [];

  return (
    <Card>
      <CardHeader className="d-flex align-items-center gap-2">
        <Building2 size={16} className="text-muted" />
        <h5 className="card-title mb-0 flex-grow-1">{tx("yetkiFirmaKullaniciSekmesi.firmaKullaniciBaglantilari")}</h5>
      </CardHeader>
      <CardBody>
        <Row className="g-3 mb-3">
          <Col md={6}>
            <FirmaSearchSelect forceLive label={tx("yetkiFirmaKullaniciSekmesi.firma")} value={firma} onChange={(f) => { setFirma(f); setUsers(null); setMsg(null); }} />
          </Col>
        </Row>
        {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}
        {msg && <Alert color="success" toggle={() => setMsg(null)}>{msg}</Alert>}
        {!firma ? (
          <p className="text-muted mb-0">{tx("yetkiFirmaKullaniciSekmesi.bagliKullanicilariniGormekIcinBir")}</p>
        ) : (
          <>
            <h6 className="mb-2">
              {firma.firmaName} <span className="text-muted font-monospace small">#{firma.firmaId}</span>{" "}{tx("yetkiFirmaKullaniciSekmesi.bagliKullanicilar")}
            </h6>
            {loading && !users ? (
              <div className="text-center py-3"><Spinner color="primary" size="sm" /></div>
            ) : (
              <div className="table-responsive mb-4">
                <Table className="align-middle mb-0" size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>{tx("yetkiFirmaKullaniciSekmesi.kullanici")}</th>
                      <th>{tx("yetkiFirmaKullaniciSekmesi.rol")}</th>
                      <th>{tx("yetkiFirmaKullaniciSekmesi.sonGiris")}</th>
                      <th className="text-center">{tx("yetkiFirmaKullaniciSekmesi.durum")}</th>
                      <th className="text-end" style={{ width: 170 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {(users || []).length === 0 && (
                      <tr><td colSpan={5} className="text-center text-muted py-3">{tx("yetkiFirmaKullaniciSekmesi.buFirmayaBagliKullaniciYok")}</td></tr>
                    )}
                    {(users || []).map((u) => (
                      <tr key={u.userId}>
                        <td>
                          <div className="fw-semibold">{fullName(u)}</div>
                          <div className="small text-muted font-monospace">{u.userLoginName} · #{u.userId}</div>
                        </td>
                        <td><div className="small">{u.userGroupName || "—"}</div><RolRozeti user={u} /></td>
                        <td className="small text-muted">{fmtDate(u.userLastLogin)}</td>
                        <td className="text-center">
                          <Badge color={u.userActive === 1 ? "success" : "secondary"} pill>{u.userActive === 1 ? tx("yetkiFirmaKullaniciSekmesi.aktif") : tx("yetkiFirmaKullaniciSekmesi.pasif")}</Badge>
                        </td>
                        <td className="text-end">
                          <Button size="sm" color="danger" outline disabled={busy !== null || isMe(u)}
                            onClick={() => act(`sil-${u.userId}`, () => removeUserFirma(u.userId, firma.firmaId),
                              tx("yetkiFirmaKullaniciSekmesi.firmadanCikarildi", { fullName: fullName(u) }))}>
                            {busy === `sil-${u.userId}` ? <Spinner size="sm" /> : <><X size={13} className="me-1" />{tx("yetkiFirmaKullaniciSekmesi.firmadanCikar")}</>}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <h6 className="mb-2">{tx("yetkiFirmaKullaniciSekmesi.buFirmayaKullaniciBagla")}</h6>
            <Label for="fk-ara" className="form-label small mb-1">{tx("yetkiFirmaKullaniciSekmesi.kullaniciAra")}</Label>
            <Input id="fk-ara" bsSize="sm" style={{ maxWidth: 420 }} placeholder={tx("yetkiFirmaKullaniciSekmesi.adKullaniciAdiEPosta")}
              value={q} onChange={(e) => setQ(e.target.value)} />
            {visibleAdaylar.length > 0 && (
              <div className="table-responsive mt-2">
                <Table size="sm" className="align-middle mb-0">
                  <tbody>
                    {visibleAdaylar.map((u) => {
                      const already = (u.firmalar || []).some((f) => f.firmaId === firma.firmaId);
                      const other = (u.firmalar || []).filter((f) => f.firmaId !== firma.firmaId);
                      return (
                        <tr key={u.userId}>
                          <td>
                            <div className="fw-semibold">{fullName(u)}</div>
                            <div className="small text-muted font-monospace">{u.userLoginName} · #{u.userId}</div>
                          </td>
                          <td><RolRozeti user={u} /></td>
                          <td style={{ minWidth: 220 }}><FirmaHucresi firmalar={u.firmalar} /></td>
                          <td className="text-end">
                            {already ? (
                              <span className="text-muted small">{tx("yetkiFirmaKullaniciSekmesi.zatenBagli")}</span>
                            ) : (
                              <Button size="sm" color="primary" disabled={busy !== null || isMe(u)}
                                title={other.length ? tx("yetkiFirmaKullaniciSekmesi.kullanicininSuAnkiFirmaBaglantisi") : undefined}
                                onClick={() => act(`bagla-${u.userId}`, () => setUserFirma(u.userId, firma.firmaId),
                                  tx("yetkiFirmaKullaniciSekmesi.firmasinaBaglandi", { fullName: fullName(u), firmaName: firma.firmaName }))}>
                                {busy === `bagla-${u.userId}` ? <Spinner size="sm" /> : <><Link2 size={13} className="me-1" />{other.length ? tx("yetkiFirmaKullaniciSekmesi.buFirmayaTasi") : tx("yetkiFirmaKullaniciSekmesi.bagla")}</>}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            <div className="form-text">
              {tx("yetkiFirmaKullaniciSekmesi.birKullanicininTekFirmasiOlur")}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}
